import React, { Fragment } from 'react';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMasterDataModel, IPresentationData } from '@tencent/slide-sdk-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import PresentationModel from '@src/slide/domain/ui-model/element-modeles/presentation-model';
import { UiDataService } from '@src/slide/domain/ui-model/service/ui-data-service';
import Loading from '@tencent/dui/lib/components/Loading';
import styles from '@com/tool-bar/menu/template-grid-menu.less';
import Menu from '@tencent/dui/lib/components/Menu';
import ScaleSlide from '@editor/src/page/index/components/canvas/scale-slide';
import { RenderPostionType } from '@src/js/constant';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { deepClone } from '@com-m/utils/object';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { importSlidesV3 } from '@com-pc/paging-selection/utils';

interface ISupportingLayoutProps {
    containerHeight: number;
    containerWidth: number;
    containerPadding: number;
    containerMarginRight: number;
    rowWidth: number;
    columns: number;
    templateWidth: number;
    templateHeight: number;
    onMenuClick();
}

export const ANIMATION_DELAY = 100;

/**
 * 配套版式面板
 */
interface ISupportingLayoutState {
    slideModelList: ISlideModel[];
    loading: boolean;
}

export class SupportingLayout extends React.Component<ISupportingLayoutProps, ISupportingLayoutState> {
    public constructor(props: ISupportingLayoutProps) {
        super(props);
        this.state = {
            slideModelList: [],
            loading: true,
        };
    }

    public componentDidMount() {
        // 不要阻塞下拉菜单动画
        setTimeout(() => {
            this.getTemplatePresentation();
        }, ANIMATION_DELAY);
    }

    public render = () => {
        const {
            rowWidth,
            containerWidth,
            containerPadding,
            containerMarginRight,
            columns,
            templateWidth,
            templateHeight,
        } = this.props;

        const containerHeight = 80;
        return <Fragment>
            <Loading visible={this.state.loading} style={{
                position: 'absolute',
                left: '50%',
                top: '150px',
                transform: 'translateX(-50%)',
                display: this.state.loading ? 'block' : 'none',
            }} />
            <div style={{ width: rowWidth }}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>{this.state.slideModelList.map((slideModel, slideIndex) => {
                    const marginTop = slideIndex < columns ? '10px' : '6px';
                    const isLastLayout = (slideIndex + 1) % columns === 0;
                    const itemStyle: React.CSSProperties = {
                        display: 'block',
                        height: containerHeight,
                        width: containerWidth,
                        padding: containerPadding,
                        position: 'relative',
                        marginTop,
                        marginRight: isLastLayout ? 0 : containerMarginRight,
                        boxSizing: 'border-box',
                        verticalAlign: 'top',
                    };
                    const layoutId = `${slideIndex}`;
                    return (
                        <div
                            className={styles.layoutContainer}
                            key={slideModel.key}
                        >
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.onClick(slideIndex);
                                }}
                            >
                                <Menu.Item id={layoutId} style={itemStyle}>
                                    {/* 此处改为幻灯片缩略图 */}
                                    <ScaleSlide
                                        width={templateWidth}
                                        height={templateHeight}
                                        slideModel={slideModel}
                                        shouldUpdate={false}
                                        isTemplate={true}
                                        region={RenderPostionType.TEMPLATE} />
                                </Menu.Item>
                            </div>
                        </div>
                    );
                })}</div>
            </div></Fragment>;
    };

    /**
     * 获取模版数据，克隆出uiData
     */
    private getTemplatePresentation = async () => {
        const selectionService = this.getSelectionService();
        const slideRenderStateManager = this.getSlideRenderManager();
        const { currentSlide } = this.getSelectionService();
        const masterDataModel = currentSlide?.getRelationMasterModel().sdkData as IMasterDataModel;

        // todo: 母版的cSld.name语义上不应该存模版id（不过目的没地用这个name），但是自定义ooxml还没开发完成，暂时代餐，开发完了一定要切成自定义字段
        const templateId = masterDataModel?.props.cSld.name;
        if (templateId) {
            this.getChangeTemplateService()?.promise()
                .then(async (module) => {
                    const presentationData = await module.getPresentationByCached(templateId);

                    const newPresentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService()!
                        .createFromData(presentationData);
                    const newSdkPresentation = newPresentationStoreService?.getPresentation();
                    const newPresentation = new PresentationModel({
                        selectionService,
                    });

                    const cloneUiDataService = new UiDataService();
                    cloneUiDataService.presentation = newPresentation;
                    cloneUiDataService.presentation.asyncConvertChildren = false;
                    cloneUiDataService.presentation.asyncConvertSlideChildren = false;
                    cloneUiDataService.setSlideRenderStateManagerDelegate(slideRenderStateManager);
                    await newPresentation.bindSDK(newSdkPresentation);
                    await cloneUiDataService?.forceConvertSlides(cloneUiDataService.presentation.children);
                    this.setState({
                        slideModelList: cloneUiDataService.presentation.children,
                        loading: false,
                    });
                });
        }
    };

    private onClick = async (slideIndex: number) => {
        const { currentSlide } = this.getSelectionService();
        const masterDataModel = currentSlide?.getRelationMasterModel().sdkData as IMasterDataModel;
        const templateId = masterDataModel?.props.cSld.name;

        // 商城上报 模板商城-模板内容-模板点击使用（点击缩略图时上报）
        tdw({
            opername: TDWOperationType.DOC_TEMPLATE_WEB,
            module: TDWModuleType.TEMPLATE_ITEM,
            action: TDWActionType.TEMPLATE_CLICK,
            ver6: templateId, // 模板id
            ver11: 2, // 缩略图点击
            ver12: 1, // 模板商城模板
            file_type: 4, // ppt
            source3: 'doc_ppt_recommend_reco', // fromPage
            ver9: 'doc_ppt_recommend_reco', // fromPage
        });

        // 插入选中页面
        this.getChangeTemplateService()?.promise()
            .then(async (module) => {
                if (!templateId) return;
                const presentationData = await module.getPresentationByCached(templateId);
                const clonePresentationData = deepClone(presentationData) as IPresentationData;
                clonePresentationData.slideList = [clonePresentationData.slideList[slideIndex]];
                clonePresentationData.masterList.forEach((master) => {
                    master.cSld.name = templateId;
                });
                importSlidesV3(clonePresentationData, this.props.onMenuClick, true);
            });
    };

    private getChangeTemplateService = () => (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
        .editableServiceLayer?.changeTemplateService;

    private getSelectionService = () => (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
        .viewableServiceLayer.selectionService;

    private getSlideRenderManager = () => (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
        .viewableServiceLayer.slideRenderStateManager;
}
